import { EBillingInterval } from "../models/i-pricing-config";

/**
 * The plan a visitor configured in the website's price calculator (`?plan=clouds:2,receivers:5;live-view;yearly`),
 * kept until the company exists and then preselected on the features page. The parameter is untrusted: only known
 * products and features pass, quantities are clamped to the sliders' range, anything else is dropped. It only
 * preselects; nothing is bought without the user saving.
 */
export interface ISelectedPlan {
	maxClouds: number;
	maxGroups: number;
	maxReceivers: number;
	maxUsers: number;
	featureIds: string[];
	interval: EBillingInterval;
}

const STORAGE_KEY: string = "zaeper.selectedPlan";
const MAX_PARAM_LENGTH: number = 300;
const MIN_QUANTITY: number = 1;
const MAX_QUANTITY: number = 100;
/** The website calculator's product ids and the licence value each one sets. */
const QUANTITIES: Record<string, keyof Pick<ISelectedPlan, "maxClouds" | "maxGroups" | "maxReceivers" | "maxUsers">> = {
	clouds: "maxClouds",
	groups: "maxGroups",
	receivers: "maxReceivers",
	users: "maxUsers"
};
const FEATURES: string[] = [ "group-sync", "live-view", "private-repository" ];

/** The plan in the parameter, or null when there is none worth keeping. */
export function parsePlanParam(raw: string | null | undefined): ISelectedPlan | null {
	if (!raw || raw.length > MAX_PARAM_LENGTH) {
		return null;
	}
	const [ quantities = "", features = "", interval = "" ] = raw.split(";");
	const plan: ISelectedPlan = {
		maxClouds: MIN_QUANTITY,
		maxGroups: MIN_QUANTITY,
		maxReceivers: MIN_QUANTITY,
		maxUsers: MIN_QUANTITY,
		featureIds: [],
		interval: interval === "yearly" ? "YEARLY" : "MONTHLY"
	};
	for (const entry of quantities.split(",")) {
		const [ id, value ] = entry.split(":");
		const field = Object.prototype.hasOwnProperty.call(QUANTITIES, id) ? QUANTITIES[id] : undefined;
		const quantity: number = Number.parseInt(value ?? "", 10);
		if (field && Number.isFinite(quantity)) {
			plan[field] = Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, quantity));
		}
	}
	plan.featureIds = FEATURES.filter(id => features.split(",").includes(id));
	return plan;
}

/** Keeps the plan of the current URL's `plan` parameter for this tab, until the features page uses it. */
export function captureSelectedPlan(search: string): void {
	const plan: ISelectedPlan | null = parsePlanParam(new URLSearchParams(search).get("plan"));
	if (!plan) {
		return;
	}
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
	} catch {
		// Without storage the features page simply opens as it is.
	}
}

export function hasSelectedPlan(): boolean {
	try {
		return sessionStorage.getItem(STORAGE_KEY) !== null;
	} catch {
		return false;
	}
}

/** The kept plan, once: it is removed as it is read. Re-checked, as storage is as untrusted as the URL. */
export function takeSelectedPlan(): ISelectedPlan | null {
	try {
		const stored: string | null = sessionStorage.getItem(STORAGE_KEY);
		sessionStorage.removeItem(STORAGE_KEY);
		if (!stored) {
			return null;
		}
		const plan = JSON.parse(stored) as Partial<ISelectedPlan>;
		return parsePlanParam([
			Object.entries(QUANTITIES).map(([ id, field ]) => `${ id }:${ Number(plan[field]) }`).join(","),
			Array.isArray(plan.featureIds) ? plan.featureIds.join(",") : "",
			plan.interval === "YEARLY" ? "yearly" : "monthly"
		].join(";"));
	} catch {
		return null;
	}
}
